
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, ExternalLink, Archive } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { NEWS_SEARCH_QUERY } from "@/utils/constants";

interface NewsItem {
  title: string;
  summary: string;
  date: string;
  category: string;
  source: string;
  url: string;
}

const POLLING_INTERVAL = 4 * 60 * 60 * 1000; // 4 hours in milliseconds

const LoadingSkeleton = () => (
  <div className="p-6 rounded-lg backdrop-blur-sm bg-white/5 border border-white/10 shadow-lg h-full">
    <div className="flex items-center justify-between mb-4">
      <Skeleton className="h-5 w-24" />
      <Skeleton className="h-5 w-32" />
    </div>
    <Skeleton className="h-6 w-3/4 mb-2" />
    <Skeleton className="h-6 w-1/2 mb-4" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-3/4 mb-4" />
    <Skeleton className="h-4 w-32" />
  </div>
);

const Index = () => {
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const { toast } = useToast();

  const fetchNews = async () => {
    try {
      setIsLoading(true);
      const query = encodeURIComponent(NEWS_SEARCH_QUERY);
      const url = `https://newsdata.io/api/1/news?apikey=pub_70308430ef510e875c33c08ca89adaa745278&q=${query}&language=en&size=4`;
      
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok || data.status === "error") {
        throw new Error(data.message || 'Failed to fetch news');
      }

      if (!data.results || data.results.length === 0) {
        console.log("No results found for query:", NEWS_SEARCH_QUERY);
        toast({
          variant: "destructive",
          title: "No Results Found",
          description: "Try modifying the search query in the admin panel.",
        });
        setIsLoading(false);
        return;
      }
      
      const formattedNews: NewsItem[] = data.results.map((article: any) => ({
        title: article.title || "Untitled",
        summary: article.description || "No summary available",
        date: article.pubDate ? new Date(article.pubDate).toISOString().split('T')[0] : "No date",
        category: determineCategory(article.title || ""),
        source: article.source_id || "Unknown source",
        url: article.link || "#"
      }));

      setNewsData(formattedNews);
      setLastUpdated(new Date());
      setRetryCount(0);
      
      toast({
        title: "News Updated",
        description: "Latest AI news has been fetched successfully.",
      });
    } catch (error) {
      console.error('Error fetching news:', error);
      
      // Implement exponential backoff for retries
      const shouldRetry = retryCount < 3;
      const retryDelay = Math.pow(2, retryCount) * 1000; // exponential backoff: 2s, 4s, 8s
      
      if (shouldRetry) {
        toast({
          title: "Retrying...",
          description: `Attempting to fetch news again in ${retryDelay/1000} seconds.`,
        });
        
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchNews();
        }, retryDelay);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch latest news. Please try again later.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const determineCategory = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('impact') || lowerTitle.includes('effect')) return 'Impact';
    if (lowerTitle.includes('research') || lowerTitle.includes('study')) return 'Research';
    if (lowerTitle.includes('free speech') || lowerTitle.includes('freedom')) return 'Free Speech';
    return 'AI & Society';
  };

  useEffect(() => {
    fetchNews();
    const intervalId = setInterval(fetchNews, POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <main className="container px-4 py-16 mx-auto">
        <div className="flex flex-col items-center justify-center space-y-8">
          <img
            src="/lovable-uploads/fade160c-5745-42c2-9cc1-82d0f4d68794.png"
            alt="Causalitas"
            className="h-12 mb-8"
          />
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            Conclusion on AI & causality
          </h1>
          <p className="max-w-2xl mx-auto text-xl text-muted-foreground">
            Exploring how large language models influence freedom of expression and societal discourse
          </p>
          <div className="flex items-center justify-center space-x-4">
            <p className="text-sm text-muted-foreground">
              Last updated: {lastUpdated.toLocaleString()}
            </p>
            <Link to="/archive">
              <Button variant="outline" size="sm" className="inline-flex items-center">
                <Archive className="w-4 h-4 mr-2" />
                View Archive
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 relative">
          {isLoading ? (
            <>
              <LoadingSkeleton />
              <LoadingSkeleton />
              <LoadingSkeleton />
              <LoadingSkeleton />
            </>
          ) : newsData.length === 0 ? (
            <div className="col-span-2 text-center py-12">
              <div className="space-y-4">
                <p className="text-lg text-muted-foreground">No news articles found.</p>
                <p className="text-sm text-muted-foreground">
                  Current search query: <code className="px-2 py-1 bg-muted rounded">{NEWS_SEARCH_QUERY}</code>
                </p>
                <Link to="/admin">
                  <Button variant="outline" size="sm">
                    Modify Search Query
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            newsData.map((news, index) => (
              <div 
                key={index}
                className="transform transition-all duration-300 hover:-translate-y-2 flex flex-col"
              >
                <div className="p-6 rounded-lg backdrop-blur-sm bg-white/5 border border-white/10 shadow-lg hover:bg-white/10 transition-all duration-300 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="secondary" className="text-xs font-medium">
                      {news.category}
                    </Badge>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      {news.date}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold leading-tight tracking-tight mb-3">
                    {news.title}
                  </h3>
                  <div className="flex-grow overflow-hidden max-h-[12rem]">
                    <p className="text-muted-foreground line-clamp-8">
                      {news.summary}
                    </p>
                  </div>
                  <a 
                    href={news.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors mt-4"
                  >
                    <span className="mr-1">Source: {news.source}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))
          )}
        </div>

        <footer className="mt-16 py-8 border-t border-border">
          <nav className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary">Terms of Service</Link>
            <Link to="/cookies" className="hover:text-primary">Cookie Policy</Link>
            <Link to="/imprint" className="hover:text-primary">Imprint</Link>
          </nav>
        </footer>
      </main>
    </div>
  );
};

export default Index;
